import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Opportunity } from '@/types';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Building2, 
  Bookmark, 
  Share2, 
  ChevronLeft,
  DollarSign,
  CheckCircle,
  Phone,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const typeLabels = {
  emploi: 'Emploi',
  stage: 'Stage',
  bourse: 'Bourse',
  formation: 'Formation',
  volontariat: 'Volontariat'
};

const typeColors = {
  emploi: 'bg-primary text-primary-foreground',
  stage: 'bg-secondary text-secondary-foreground',
  bourse: 'bg-success text-success-foreground',
  formation: 'bg-accent text-accent-foreground',
  volontariat: 'bg-destructive text-destructive-foreground'
};

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOpportunity = async () => {
      try {
        const { data, error } = await supabase
          .from('opportunities')
          .select('*')
          .eq('id', id)
          .eq('status', 'approved')
          .maybeSingle();

        if (error) throw error;

        if (data) {
          const transformedData: Opportunity = {
            id: data.id,
            title: data.title,
            company: data.company,
            type: data.type,
            location: data.location,
            arrondissement: data.arrondissement,
            description: data.description,
            responsibilities: data.responsibilities,
            requirements: data.requirements,
            salary: data.salary || data.scholarship_amount,
            deadline: data.deadline ? new Date(data.deadline) : new Date(),
            publishedAt: new Date(data.created_at),
            applicationUrl: data.application_url,
            phoneNumber: data.contact_phone,
          };
          setOpportunity(transformedData);
        }
      } catch (error: any) {
        console.error('Erreur lors du chargement de l\'opportunité:', error);
        toast.error('Erreur lors du chargement de l\'opportunité');
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunity();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-poppins font-bold text-foreground mb-4">
            Opportunité introuvable
          </h1>
          <p className="text-muted-foreground font-inter mb-8">
            L'opportunité que vous recherchez n'existe pas ou a été supprimée.
          </p>
          <Link to="/opportunities">
            <Button variant="default">
              Retour aux opportunités
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: opportunity.title,
        text: `${opportunity.title} chez ${opportunity.company}`,
        url: window.location.href
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Link to="/opportunities" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6 font-inter">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Retour aux opportunités
        </Link>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge className={typeColors[opportunity.type]}>
                      {typeLabels[opportunity.type]}
                    </Badge>
                    {opportunity.featured && (
                      <Badge className="bg-gradient-gold text-primary">
                        À la une
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-poppins font-bold text-foreground mb-2">
                    {opportunity.title}
                  </h1>
                  <p className="text-lg text-muted-foreground font-inter">{opportunity.company}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span className="font-inter">
                    {opportunity.arrondissement ? `${opportunity.arrondissement}, ${opportunity.location}` : opportunity.location}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span className="font-inter">
                    Publié le {format(opportunity.publishedAt, 'dd MMMM yyyy', { locale: fr })}
                  </span>
                </div>
                {opportunity.salary && (
                  <div className="flex items-center gap-1 text-success">
                    <DollarSign className="h-4 w-4" />
                    <span className="font-inter font-semibold">{opportunity.salary}</span>
                  </div>
                )}
              </div>
            </Card>
            
            {/* Description */}
            <Card className="p-6">
              <h2 className="text-xl font-poppins font-semibold text-foreground mb-4">
                Description du poste
              </h2>
              <p className="text-muted-foreground font-inter whitespace-pre-line">
                {opportunity.description}
              </p>
            </Card>
            
            {/* Responsibilities */}
            {opportunity.responsibilities && opportunity.responsibilities.length > 0 && (
              <Card className="p-6">
                <h2 className="text-xl font-poppins font-semibold text-foreground mb-4">
                  Responsabilités
                </h2>
                <ul className="space-y-2">
                  {opportunity.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground font-inter">
                      <CheckCircle className="h-5 w-5 text-success mt-0.5 shrink-0" />
                      <span>{resp}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            
            {/* Requirements */}
            {opportunity.requirements && opportunity.requirements.length > 0 && (
              <Card className="p-6">
                <h2 className="text-xl font-poppins font-semibold text-foreground mb-4">
                  Qualifications requises
                </h2>
                <ul className="space-y-2">
                  {opportunity.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground font-inter">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-20">
              {/* Deadline Alert */}
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-destructive mb-1">
                  <Clock className="h-5 w-5" />
                  <span className="font-poppins font-semibold">Date limite</span>
                </div>
                <p className="text-lg font-inter font-bold text-destructive">
                  {format(opportunity.deadline, 'dd MMMM yyyy', { locale: fr })}
                </p>
                <p className="text-sm text-muted-foreground font-inter mt-1">
                  Plus que {Math.ceil((opportunity.deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} jours
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="space-y-3">
                {opportunity.applicationUrl ? (
                  <a 
                    href={opportunity.applicationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button size="lg" className="w-full">
                      Postuler maintenant
                    </Button>
                  </a>
                ) : (
                  <Button size="lg" className="w-full" disabled>
                    Postuler maintenant
                  </Button>
                )}
                {opportunity.phoneNumber && (
                  <a href={`tel:${opportunity.phoneNumber.replace(/\s/g, '')}`} className="block">
                    <Button variant="secondary" size="lg" className="w-full">
                      <Phone className="h-5 w-5 mr-2" />
                      Appeler {opportunity.phoneNumber}
                    </Button>
                  </a>
                )}
                <Button variant="outline" size="lg" className="w-full">
                  <Bookmark className="h-5 w-5 mr-2" />
                  Sauvegarder
                </Button>
                <Button variant="ghost" size="lg" className="w-full" onClick={handleShare}>
                  <Share2 className="h-5 w-5 mr-2" />
                  Partager
                </Button>
              </div>
              
              {/* Company Info */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-poppins font-semibold text-foreground mb-3">
                  À propos de {opportunity.company}
                </h3>
                <p className="text-sm text-muted-foreground font-inter mb-4">
                  Découvrez plus d'opportunités de cette organisation.
                </p>
                <Link to={`/company/${opportunity.company}`}>
                  <Button variant="secondary" size="sm" className="w-full">
                    Voir le profil
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}